import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pagination } from 'antd';
import { useDispatch } from 'react-redux';
import { usePublicState, setPage } from '../../../store/slices/publicSlice';
import SectionDesc from '../../../components/atoms/SectionDesc';
import SectionTitle from '../../../components/atoms/SectionTitle';
import Button from '../../../components/atoms/Button';
import Search from '../../../components/categories/Search';
import EmptyState from '../../../components/atoms/EmptyState';
import CategoriesSkeleton from '../../../components/categories/list/CategoriesSkeleton';
import { Category, SubCategory } from '../../../types/lookups';
import { useGetOperatorCategoriesQuery } from '../../../store/services/lookups';
import CategoryRow from './components/CategoryRow';
import AddCategoryModal from './components/AddCategoryModal';
import AddSubCategoryModal from './components/AddSubCategoryModal';
import EditCategoryModal from './components/EditCategoryModal';
import EditSubCategoryModal from './components/EditSubCategoryModal';

export default function CategoriesPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { search, page } = usePublicState();

  const { data: fetchedCategories, isFetching } = useGetOperatorCategoriesQuery(
    {
      page,
      search: search?.category || undefined,
    }
  );

  const categories = fetchedCategories?.content || [];

  // Modal States
  const [isAddCategoryOpen, setIsAddCategoryOpen] = useState(false);
  const [activeCategoryForAddSub, setActiveCategoryForAddSub] =
    useState<Category | null>(null);
  const [activeCategoryForEdit, setActiveCategoryForEdit] =
    useState<Category | null>(null);
  const [activeSubForEdit, setActiveSubForEdit] = useState<{
    parentCatId: string;
    subCat: SubCategory;
  } | null>(null);




  const handleEditCategory = () => {
    setActiveCategoryForEdit(null);
  };

  const handleEditSubCategory = () => {
    setActiveSubForEdit(null);
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <SectionTitle text={t('home.categories')} />
          <SectionDesc text={t('home.categories-desc')} />
        </div>
        <Button
          className="rounded-xl px-6 py-2"
          onClick={() => setIsAddCategoryOpen(true)}
        >
          {t('categories.addNewCategory')}
        </Button>
      </div>
      <div className="space-y-2">
        <div className="bg-white rounded-xl p-6 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-800">
              {t('home.categories')}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {t('categories.displayAllRequests')}
            </p>
          </div>
          <div className="">
            <Search searchName="category" />
          </div>
        </div>

        {isFetching ? (
          <CategoriesSkeleton />
        ) : categories.length > 0 ? (
          categories.map((category) => (
            <CategoryRow
              key={category.id}
              category={category}
              onEditCategory={() => setActiveCategoryForEdit(category)}
              onAddSubCategory={() => setActiveCategoryForAddSub(category)}
              onEditSubCategory={(subCat) =>
                setActiveSubForEdit({ parentCatId: category.id, subCat })
              }
            />
          ))
        ) : (
          <EmptyState />
        )}
      </div>

      {/* Add Category Modal */}
      {isAddCategoryOpen && (
        <AddCategoryModal
          isOpen={isAddCategoryOpen}
          onClose={() => setIsAddCategoryOpen(false)}
          onSuccess={() => setIsAddCategoryOpen(false)}
        />
      )}

      {/* Add Sub Category Modal */}
      {activeCategoryForAddSub && (
        <AddSubCategoryModal
          isOpen={!!activeCategoryForAddSub}
          onClose={() => setActiveCategoryForAddSub(null)}
          onSuccess={() => setActiveCategoryForAddSub(null)}
          parentCategoryId={activeCategoryForAddSub.id}
          parentCategoryName={`${activeCategoryForAddSub.nameEn} | ${activeCategoryForAddSub.nameAr}`}
        />
      )}

      {/* Edit Category Modal */}
      {activeCategoryForEdit && (
        <EditCategoryModal
          isOpen={!!activeCategoryForEdit}
          onClose={() => setActiveCategoryForEdit(null)}
          onSuccess={() => setActiveCategoryForEdit(null)}
          category={activeCategoryForEdit}
        />
      )}

      {/* Edit Sub Category Modal */}
      {activeSubForEdit && (
        <EditSubCategoryModal
          isOpen={!!activeSubForEdit}
          onClose={() => setActiveSubForEdit(null)}
          onSuccess={() => setActiveSubForEdit(null)}
          subCategory={activeSubForEdit.subCat}
        />
      )}

      {/* Pagination */}
      {Number(fetchedCategories?.totalElements) > (fetchedCategories?.size || 20) && !isFetching && (
        <div className="flex items-center justify-center my-6 bg-light-surface-primary py-4" dir="ltr">
          <Pagination
            className="flex justify-center"
            total={fetchedCategories?.totalElements || 0}
            pageSize={fetchedCategories?.size || 20}
            defaultCurrent={page + 1}
            current={page + 1}
            onChange={(e) => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
              dispatch(setPage(e - 1));
            }}
            showSizeChanger={false}
          />
        </div>
      )}
    </div>
  );
}
